import type { CardOpts, UIApi } from '@/core/context';
import { DEPT_NAME, type Game } from '@/core/game';
import { bus } from '@/core/bus';
import { Save } from '@/core/save';
import { decodeFull, encodeFull, isShortCode } from '@/core/codes';
import { fmtClock, fmtDuration } from '@/core/time';
import { defaultAppearance, type Appearance } from '@/core/state';
import { assetUrl, bustUrl, fallbackAvatar, hasAsset, portraitUrl } from '@/assets/assetManager';
import { setSound } from '@/audio/sfx';
import { keyText, isTouchMode } from './touch';
import { activeCase, objective } from '@/systems/cases';
import { currentMission, missionHint, missionKind, missionStep, targetName, takeJob, canRetry, retryMission, KIND_NAME, type MissionKind } from '@/systems/missions';
import { handStack, HAND_MAX, shelfLeft, takeFromShelf, returnables, returnToShelf } from '@/systems/carry';
import { levelInfo, menuToday, paycheck, unlockRandomRecipe, xpToNext } from '@/systems/progress';
import { completion } from '@/systems/production';
import { evaluate } from '@/systems/ending';
import { check } from '@/systems/achievements';
import { FLOOR_COLOR, LEVEL_NAME, LEVEL_TINT, floorKind } from '@/world/floorBuilder';
import { SKIN } from '@/world/chibi';
import { $, clear, h, modal } from './dom';
import { drawMinimap } from './minimap';
import { tr, lang, setLang } from '@/i18n';
import { flowBlocked, flowTodos, lineStatus } from '@/systems/flow';
import { openFeedback } from './feedback';
import { openWork } from '@/minigames/window';
import { trainingGame } from './training';
import { gameNow, hasGame, loadGame } from '@/minigames/registry';
import type { MiniResult } from '@/minigames/types';
import type { FeedbackKind } from '@/feedback/shared';
import { jobSites, stepNeeds, type JobSite } from '@/systems/worksites';
import { isNewStyle, setArtStyle } from './artStyle';
import { floorColorNew } from '@/world/palette';

declare const __APP_VERSION__: string; declare const __COMMIT__: string;
const DEPT_ICON: Record<string, string> = { warehouse: '📦', purchasing: '🧾', planning: '📅', qa: '🔬', rd: '🧑‍🍳', production: '🏭', engineering: '🔧', facility: '🍱', hr: '🪪' };

/** v1.1.1 手機：試著全螢幕＋鎖橫向（瀏覽器不支援就算了，直拿時畫面會請玩家轉橫） */
function lockLandscape(): void {
  if (!matchMedia('(pointer:coarse)').matches) return;
  try {
    const el = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => void };
    const p = document.fullscreenElement ? Promise.resolve() : (el.requestFullscreen?.() ?? Promise.resolve(el.webkitRequestFullscreen?.()));
    Promise.resolve(p).then(() => (screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> }).lock?.('landscape')).catch(() => { /* 不支援 */ });
  } catch { /* 不支援 */ }
}
/** v1.1.1「了解更多資訊」：預設收起來，點了才展開 */
export function moreInfo(text?: string, extra?: HTMLElement | null): HTMLElement {
  return h('details', { class: 'more-info' }, h('summary', {}, tr("了解更多資訊")), text ? h('p', { class: 'text small' }, text) : null, extra ?? null);
}

export class UI implements UIApi {
  private open = false;
  private choiceBtns: HTMLButtonElement[] = [];
  private toastTimer = 0;
  private modalOpen: string | null = null;

  constructor(private g: Game) {
    bus.on('toast', m => this.toast(m));
    bus.on('stateChanged', () => this.refresh());
    bus.on('floorChanged', () => this.refresh());
    addEventListener('keydown', e => this.onKey(e));
    $('btnOrders').onclick = () => this.orders();
    $('btnCodex').onclick = () => this.codex();
    $('btnBag').onclick = () => this.bag();
    $('btnMenu').onclick = () => this.menu();
    $('prompt').onclick = () => g.interact();
    $('minimap').onclick = () => this.mapModal();
    $('tasksToggle').onclick = () => this.toggleTasks();
    $('btnJobs').onclick = () => this.jobBoard();
    $('btnWaypoint').onclick = () => { if (this.g.day) this.g.day.waypoint = null; this.g.updateSigns(); this.refresh(); };
    $('btnHint').onclick = () => this.hint();
    $('lvInfo').onclick = () => this.codex('me');
    $('btnRun').onclick = () => { g.runToggle = !g.runToggle; ($('btnRun') as HTMLElement).blur(); $('c').focus({ preventScroll: true }); this.refresh(); this.toast(g.runToggle ? tr("🏃 跑步模式（生產區被品保看到會被警告）") : tr("🚶 走路模式")); };
  }

  // ---------------------------------------------------------------- 對話卡
  isOpen() { return this.open || !!this.modalOpen; }
  card(o: CardOpts): void {
    o = { ...o, text: keyText(o.text), title: o.title && keyText(o.title), sections: o.sections?.map(x => ({ ...x, items: x.items.map(keyText) })) };
    this.open = true;
    $('prompt').hidden = true;
    const panel = $('panel'); clear(panel);
    panel.className = 'panel' + (o.wide ? ' wide' : '');
    const npc = o.npc ? this.g.c.npcById.get(o.npc) : undefined;
    // v1.4 新版：有半身立繪就用大張的（生產區裡、穿工作服的人用「口罩＋網帽」的共用立繪）
    const inProd = this.g.day ? (this.g.rt().zoneAt(...this.g.playerPos())?.level ?? -1) >= 0 : false;
    const bust = npc && isNewStyle() ? bustUrl(npc, o.expression, inProd) : null;
    const img = npc ? (bust ?? portraitUrl(npc.portrait, o.expression) ?? fallbackAvatar(npc.name, npc.color)) : null;
    panel.classList.toggle('has-bust', !!bust);
    const head = h('div', { class: 'who' },
      img ? h('img', { class: 'portrait' + (bust ? ' bust' : ''), src: img, alt: npc!.name, width: bust ? 120 : 72, height: bust ? 160 : 72 }) : h('span', { class: 'swatch', style: { background: o.color ?? '#f2b134' } }),
      h('div', { class: 'who-text' },
        h('div', {}, h('b', {}, o.name), o.badge ? h('span', { class: 'badge', style: { background: o.badge.color } }, o.badge.text) : null),
        o.role ? h('small', {}, o.role) : null));
    panel.append(head);
    if (o.title) panel.append(h('div', { class: 'card-title' }, o.title));
    if (o.text) panel.append(h('p', { class: 'text' }, o.text));
    let box: HTMLElement | null = null;
    if (o.sections?.length) {
      box = h('div', { class: 'sections' });
      for (const s of o.sections) box.append(h('div', { class: `sec ${s.tone ?? ''}` }, h('div', { class: 'sec-label' }, s.label), h('ul', {}, ...s.items.filter(Boolean).map(i => h('li', {}, i)))));
      if (!o.foldSections) { panel.append(box); box = null; }
    }
    if (o.more || box) panel.append(moreInfo(o.more, box));
    const choices = h('div', { class: 'choices' });
    this.choiceBtns = o.choices.map((c, i) => {
      const b = h('button', { type: 'button', class: `choice ${c.tone ?? ''}` }, h('span', { class: 'k' }, String(i + 1)),
        h('span', { class: 'ct' }, c.text, c.note ? h('small', { class: 'note' }, c.note) : null));
      b.onclick = () => { bus.emit('sfx', 'click'); c.fn(); };
      choices.append(b); return b;
    });
    panel.append(choices);
    $('dialog').hidden = false;
    panel.style.animation = 'none'; void panel.offsetWidth; panel.style.animation = '';
    panel.scrollTop = 0;
    this.choiceBtns[0]?.focus({ preventScroll: true });
    this.refresh();
  }
  close(): void { this.open = false; this.choiceBtns = []; $('dialog').hidden = true; $('c').focus({ preventScroll: true }); this.refresh(); }
  toast(msg: string): void {
    const t = $('toast'); t.textContent = keyText(msg); t.classList.add('show');
    clearTimeout(this.toastTimer); this.toastTimer = window.setTimeout(() => t.classList.remove('show'), 3400);
  }
  private onKey(e: KeyboardEvent) {
    const k = e.key.toLowerCase();
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement)?.tagName)) { if (this.modalOpen && k === 'escape') this.closeModal(); return; }
    if (this.modalOpen && k === 'escape') { this.closeModal(); e.preventDefault(); return; }
    if (this.open) {
      if (/^[1-9]$/.test(k) && this.choiceBtns[+k - 1]) { this.choiceBtns[+k - 1].click(); e.preventDefault(); }
      else if ((k === 'e') && this.choiceBtns.length === 1) { this.choiceBtns[0].click(); e.preventDefault(); }
      return;
    }
    if (!this.g.running || this.g.paused || this.modalOpen) return;
    if (k === 'o') this.orders(); else if (k === 'h') this.codex(); else if (k === 'i' || k === 'b') this.bag(); else if (k === 'm') this.mapModal();
    else if (k === 'escape') this.menu(); else if (k === 't' || k === 'j') this.toggleTasks(); else if (k === 'q') this.hint();
  }

  // ---------------------------------------------------------------- HUD
  refresh(): void {
    const g = this.g, d = g.day; if (!d) return;
    $('clock').textContent = fmtClock(d.clock);
    $('clock').classList.toggle('late', d.clock >= d.shiftEnd);
    const role = g.c.roleById.get(d.roleId)!;
    $('shiftInfo').textContent = tr("第 {0} 天・{1}・{2}・{3} {4}–{5}", d.day, role.deptName, role.name, role.shift.label, role.shift.start, role.shift.end);
    const L = levelInfo(g.c.game.progression, g.profile.xp ?? 0);
    const lvb = $('lvInfo'); clear(lvb);
    lvb.append(h('b', {}, `Lv ${L.lv}`), ` ${L.title} `, h('i', {}, h('em', { style: { width: `${L.need ? Math.round(L.into / L.need * 100) : 100}%` } })), tr(" 🎟️ {0}　💰 ${1}", g.profile.points ?? 0, (g.profile.salary ?? 0).toLocaleString()));
    $('objective').textContent = keyText(objective(g));
    const f = g.c.floorById.get(d.floor)!; const z = g.stage.rt?.floor.zones.find(x => x.id === d.zone);
    const fb = $('floorBadge'); clear(fb);
    fb.append(h('b', {}, d.floor), ' ', z?.name ?? f.name);
    if (z) fb.append(h('span', { class: 'lv', style: { background: LEVEL_TINT[z.level] } }, z.cold ? (z.cold === 'frozen' ? tr("冷凍") : tr("冷藏")) : LEVEL_NAME[z.level]));
    fb.append(h('span', { class: 'lv dress' + (d.dressed ? ' on' : '') }, d.dressed ? tr("🥼 已換裝") : tr("👕 便服")));
    if (d.pushing) fb.append(h('span', { class: 'lv cart' }, tr("🛒 推車中")));
    if (d.holding) { const hs = handStack(d); fb.append(h('span', { class: 'lv cart' }, d.holding.kind === 'cart' ? tr("🙌 拿著烏龜車") : `🧺 ${g.c.ingredientById.get(d.baskets[d.holding.id]?.content)?.name ?? tr("籃子")}${hs.length > 1 ? tr(" 等 {0} 籃", hs.length) : ''}`)); }
    if (d.dressed && z && z.level >= 0) fb.append(h('span', { class: 'lv dress' + (d.washed ? ' on' : z.level >= 1 ? ' bad' : '') }, d.washed ? tr("🧼 已洗手") : z.level >= 1 ? tr("⚠️ 沒洗手！") : tr("🧼 還沒洗手")));
    $('btnRun').textContent = g.runToggle ? tr("🏃 跑") : tr("🚶 走");
    const P = d.player, F = d.factory;
    const bars: [string, number, string, boolean][] = [[tr("體力"), P.hp, '#3f9e6c', false], [tr("疲勞"), P.fatigue, '#e07b39', true], [tr("水分"), P.hydration, '#5aa9e6', false], [tr("專注"), P.focus, '#8fa7d6', false], [tr("壓力"), P.stress, '#d9534a', true]];
    const meters = $('meters'); clear(meters);
    for (const [label, v, color, bad] of bars) {
      const warn = bad ? v > 70 : v < 30;
      meters.append(h('div', { class: 'meter' + (warn ? ' warn' : ''), title: `${label} ${Math.round(v)}` }, h('span', {}, label), h('i', {}, h('em', { style: { width: `${v}%`, background: color } }))));
    }
    const comp = completion(g);
    const kpis = $('kpis'); clear(kpis);
    const kp = (l: string, v: string, warn = false) => h('div', { class: 'kpi' + (warn ? ' warn' : '') }, h('span', {}, l), h('b', {}, v));
    kpis.append(kp(tr("訂單"), `${Math.round(comp * 100)}%`, comp < (d.clock - d.shiftStart) / (d.shiftEnd - d.shiftStart) * 0.7),
      kp(tr("食安"), String(Math.round(F.foodSafety)), F.foodSafety < 60), kp(tr("稽核"), String(Math.round(F.audit)), F.audit < 85),
      kp(tr("信任"), String(Math.round(F.trust)), F.trust < 45), kp(tr("罰款"), `$${F.fines}`, F.fines > 0));
    $('bagCount').textContent = String(g.profile.items.length + d.items.held.length + d.bag.length + (d.pack?.length ?? 0));
    this.renderTasks();
    if (!this.isOpen() && g.running && !g.paused) {
      const n = g.nearest();
      if (n) { $('promptText').textContent = n.label + (g.targetCount() > 1 ? tr("　（Tab 換一個）") : ''); $('prompt').hidden = false; }
      else if (d.holding) { $('promptText').textContent = d.holding.kind === 'cart' ? tr("放下烏龜車") : tr("🧺 放下（要放在車上或架子上）"); $('prompt').hidden = false; }
      else $('prompt').hidden = true;
    }
    drawMinimap($('minimap') as HTMLCanvasElement, g);
  }

  // ---------------------------------------------------------------- 任務欄（v0.5：隨時可以打開／收起，按 T 或 J）
  toggleTasks(open?: boolean): void {
    const s = this.g.profile.settings;
    s.tasksOpen = open ?? !s.tasksOpen; Save.saveProfile(this.g.profile);
    this.renderTasks();
  }
  private renderTasks(): void {
    const g = this.g, d = g.day; if (!d) return;
    const open = g.profile.settings.tasksOpen !== false;
    document.body.classList.toggle('tasks-collapsed', !open);
    $('tasksToggle').setAttribute('aria-expanded', String(open));
    $('tasksCaret').textContent = open ? '▾' : '▸';
    const all = d.missionQueue.map(id => g.c.missionById.get(id)!);
    const doneN = all.filter(m => d.missions[m.id].done).length;
    $('tasksCount').textContent = `${doneN}/${all.length}`;
    const list = $('tasks'); clear(list);
    if (!open) return;
    const cur = currentMission(g);
    const ICON: Record<MissionKind, string> = { daily: '📅', main: '⭐', side: '🗺️', special: '⚡' };
    for (const kind of ['daily', 'main', 'side', 'special'] as MissionKind[]) {
      const ms = all.filter(m => missionKind(g, m) === kind); if (!ms.length) continue;
      list.append(h('li', { class: `t-head k-${kind}` }, `${ICON[kind]} ${KIND_NAME[kind]}`));
      for (const m of ms) {
        const pr = d.missions[m.id]; const st = missionStep(g, m.id);
        const li = h('li', { class: `t-item k-${kind}` + (pr.done ? ' done' : cur?.m.id === m.id ? ' now' : ''), role: 'button', tabindex: '0', title: tr("點一下看全部步驟") },
          h('span', { class: 't-title' }, `${pr.failed ? '✘' : pr.done ? '✔' : d.focus === m.id ? '🎯' : m.icon} ${m.title}`, h('small', {}, pr.failed ? (canRetry(g, m.id) ? tr(" 失敗・可重來") : tr(" 失敗")) : pr.done ? tr(" 完成") : ` ${pr.step}/${m.steps.length}`)),
          !pr.done && st ? h('span', { class: 't-step' }, `${st.at ? tr("🕘 {0}｜", st.at) : ''}${st.floor === d.floor ? '' : tr("{0}・", st.floor)}${st.say}`) : null);
        li.onclick = () => this.missionDetail(m.id);
        li.onkeydown = e => { if ((e as KeyboardEvent).key === 'Enter') this.missionDetail(m.id); };
        list.append(li);
      }
    }
    const todos = flowTodos(g);
    if (todos.length) {
      list.append(h('li', { class: 't-head k-flow' }, tr("📻 線邊狀況")));
      for (const t of todos) list.append(h('li', { class: 't-item k-flow now' }, t));
    }
    if (d.caseQueue.length) {
      list.append(h('li', { class: 't-head k-case' }, tr("⚠️ 異常案件")));
      d.caseQueue.forEach((id, i) => {
        const k = g.c.caseById.get(id)!; const pr = d.cases[id];
        const state = pr.stage === 'done' ? (pr.grade === 'good' ? '✔' : '✘') : i === d.activeCase ? '▶' : '·';
        list.append(h('li', { class: 't-item k-case' + (pr.stage === 'done' ? ' done' : i === d.activeCase ? ' now' : '') }, tr("{0} {1}・{2}", state, k.id, i <= d.activeCase ? k.title : tr("？？？"))));
      });
    }
    // v1.3.1「作業在哪裡」移到任務欄標題旁（btnJobs）；帶路中顯示「📍 地點 ✕」，點一下取消
    const wp = d.waypoint; const wb = $('btnWaypoint') as HTMLButtonElement;
    wb.hidden = !wp; if (wp) { clear(wb); wb.append(h('span', { class: 'wp-l' }, tr("📍 {0}・{1}", wp.floor, wp.label)), h('b', {}, '✕')); wb.title = tr("取消帶路"); }
  }
  /** 點任務：看全部步驟（做過的打勾、現在這一步、背包需要的東西） */
  missionDetail(id: string): void {
    const g = this.g, d = g.day, m = g.c.missionById.get(id)!; const pr = d.missions[id];
    const kind = missionKind(g, m);
    const body = h('div', {},
      h('p', { class: 'lead' }, m.summary),
      h('ol', { class: 'steps' }, ...m.steps.map((st, i) => h('li', { class: i < pr.step || pr.done ? 'done' : i === pr.step ? 'now' : '' },
        h('b', {}, tr("{0}{1}{2}・{3}", i < pr.step || pr.done ? '✔ ' : i === pr.step ? '▶ ' : '', st.at ? tr("{0}｜", st.at) : '', st.floor, targetName(g, st))),
        h('span', {}, st.say),
        ...stepNeeds(st).map(id => h('small', {}, tr("🎒 要帶：{0} {1}", g.c.packById.get(id)?.icon ?? '', g.c.packById.get(id)?.name ?? id))),
        st.give ? h('small', {}, tr("🎒 會拿到：{0} {1}", g.c.packById.get(st.give)?.icon ?? '', g.c.packById.get(st.give)?.name ?? st.give)) : null))),
      !pr.done && m.steps[pr.step] ? h('p', { class: 'small' }, `💡 ${missionHint(g, m.steps[pr.step])}`) : null,
      m.reward?.text ? h('p', { class: 'small' }, `🎁 ${m.reward.text}`) : null);
    // v1.1 跟著這個任務走／失敗的再挑戰一次（一天一次）
    const row = h('div', { class: 'row' });
    if (!pr.done) { const b = h('button', { type: 'button', class: 'btn secondary' }, d.focus === id ? tr("🎯 正在跟著這個任務") : tr("🎯 跟著這個任務走（顯示「!」和導航）")); b.onclick = () => { d.focus = id; this.closeModal(); g.updateSigns(); this.refresh(); this.toast(tr("🎯 改成跟著「{0}」", m.title)); }; row.append(b); }
    if (pr.failed) {
      if (canRetry(g, id)) { const b = h('button', { type: 'button', class: 'btn primary' }, tr("🔁 再挑戰一次（今天只能重來一次）")); b.onclick = () => { this.closeModal(); retryMission(g, id); this.refresh(); }; row.append(b); }
      else row.append(h('p', { class: 'small' }, tr("今天已經重來過一次了，明天再試。")));
    }
    if (row.childElementCount) body.append(row);
    this.openModal('mission', tr("{0} {1}｜{2}", m.icon, KIND_NAME[kind], m.title), body);
  }

  // ---------------------------------------------------------------- 開始畫面：部門 → 職務（可隨機）
  showStart(): void {
    this.g.stop();
    $('hud').hidden = true; $('prompt').hidden = true; $('dialog').hidden = true; this.open = false;
    const c = this.g.c, p = this.g.profile;
    const scr = $('start'); clear(scr); scr.hidden = false;
    const saved = Save.loadDay();
    const depts = [...new Map(c.roles.map(r => [r.dept, r.deptName])).entries()];
    let dept: string | 'any' = c.roleById.get(p.roleId ?? '')?.dept ?? 'production';
    let role: string | 'any' = p.roleId ?? 'any';
    const deptRow = h('div', { class: 'depts', role: 'radiogroup', 'aria-label': tr("選擇部門") });
    const roleGrid = h('div', { class: 'roles', role: 'radiogroup', 'aria-label': tr("選擇職務") });
    const render = () => {
      clear(deptRow); clear(roleGrid);
      const anyBtn = h('button', { type: 'button', class: 'dept', 'aria-pressed': String(dept === 'any') }, tr("🎲 完全隨機"));
      anyBtn.onclick = () => { dept = 'any'; role = 'any'; render(); }; deptRow.append(anyBtn);
      for (const [id, name] of depts) { const b = h('button', { type: 'button', class: 'dept', 'aria-pressed': String(dept === id) }, `${DEPT_ICON[id] ?? ''} ${name}`); b.onclick = () => { dept = id; role = 'any'; render(); }; deptRow.append(b); }
      if (dept === 'any') { roleGrid.append(h('p', { class: 'small' }, tr("從 22 種職務裡隨機抽一個。今天會是什麼班？"))); return; }
      const r0 = h('button', { type: 'button', class: 'role', 'aria-pressed': String(role === 'any') }, h('b', {}, tr("🎲 這個部門隨機")), h('span', {}, tr("只選部門，職務交給命運。")));
      r0.onclick = () => { role = 'any'; render(); }; roleGrid.append(r0);
      for (const r of c.roles.filter(x => x.dept === dept)) {
        const b = h('button', { type: 'button', class: 'role', 'aria-pressed': String(r.id === role) },
          h('b', {}, r.name), h('small', {}, tr("{0} {1}–{2}・{3}", r.shift.label, r.shift.start, r.shift.end, r.startFloor)), h('span', {}, r.description));
        b.onclick = () => { role = r.id; render(); }; roleGrid.append(b);
      }
    };
    render();
    const pick = () => {
      if (role !== 'any') return role;
      const pool = c.roles.filter(r => dept === 'any' || r.dept === dept);
      return pool[Math.floor(Math.random() * pool.length)].id;
    };
    const cleared = Object.values(p.cleared).filter(x => x.grade === 'good').length;
    const btnNew = h('button', { type: 'button', class: 'btn primary' }, tr("開始第 {0} 天", p.day));
    // v1.0 上班模式：正式上班／自由模式（實習生）＋新手教學（教育訓練）
    let mode: 'work' | 'intern' = 'work';
    let tutorial = !(p.counters.trained ?? 0);
    const modeRow = h('div', { class: 'depts' });
    const drawMode = () => {
      modeRow.textContent = '';
      for (const [v, t, tip] of [['work', tr("🏭 正式上班"), tr("有每日任務、主線任務；下班前沒做完自己的工作會被罵。")], ['intern', tr("🎒 自由模式（實習生）"), tr("沒有主線任務，到處看看、做支線。但實習生一樣屬於一個部門：跑出自己的區域被主管抓到會被罵爛。")]] as const) {
        const b = h('button', { type: 'button', class: 'chip', 'aria-pressed': String(mode === v), title: tip }, t); b.onclick = () => { mode = v; drawMode(); }; modeRow.append(b);
      }
      modeRow.append(h('div', { class: 'small', style: 'flex-basis:100%' }, mode === 'work' ? tr("有每日任務、主線任務；下班前沒做完自己的工作會被罵。") : tr("沒有主線任務，到處看看、做支線。但實習生一樣屬於一個部門：跑出自己的區域被主管抓到會被罵爛。")));
    };
    drawMode();
    const tut = h('label', { class: 'check' }, h('input', { type: 'checkbox', ...(tutorial ? { checked: true } : {}) }), tr(" 🎓 新手教學（先到 4F 找人資 Grace 上教育訓練；可以不勾＝跳過）"));
    (tut.firstChild as HTMLInputElement).onchange = e => { tutorial = (e.target as HTMLInputElement).checked; };
    btnNew.onclick = () => { scr.hidden = true; this.dayIntro(pick(), { mode, tutorial }); };
    const cont = saved && !saved.day.ended ? h('button', { type: 'button', class: 'btn secondary' }, tr("繼續：第 {0} 天 {1}（{2}）", saved.day.day, fmtClock(saved.day.clock), c.roleById.get(saved.day.roleId)?.name)) : null;
    if (cont) cont.onclick = () => { if (this.g.resumeDay()) { scr.hidden = true; this.enter(); } else { this.toast(tr("這份存檔已不相容（內容改版），請開始新的一天。")); this.showStart(); } };
    const codeBtn = h('button', { type: 'button', class: 'btn secondary' }, tr("🔢 輸入進度代碼"));
    codeBtn.onclick = () => this.codeEntry();
    const look = h('button', { type: 'button', class: 'linkbtn' }, tr("👤 修改人物樣式"));
    look.onclick = () => this.appearance();
    const sound = h('label', { class: 'check' }, h('input', { type: 'checkbox', ...(p.settings.sound ? { checked: true } : {}) }), tr(" 音效"));
    (sound.firstChild as HTMLInputElement).onchange = e => { p.settings.sound = (e.target as HTMLInputElement).checked; setSound(p.settings.sound); Save.saveProfile(p); };
    const reset = h('button', { type: 'button', class: 'linkbtn' }, tr("清除全部存檔"));
    let armed = false;
    reset.onclick = () => { if (!armed) { armed = true; reset.textContent = tr("再按一次確認清除"); return; } Save.resetAll(); location.reload(); };
    const achN = Object.keys(p.achievements).length;
    const langSw = this.langSwitch();
    scr.append(h('div', { class: 'card start-card' }, langSw,
      h('div', { class: 'kicker' }, tr("食品工廠情境模擬")),
      h('h1', {}, c.game.title), h('div', { class: 'subtitle' }, c.game.subtitle),
      h('p', { class: 'intro' }, c.game.tagline),
      h('div', { class: 'progress' }, tr("已破案件 {0}/{1}・危害圖鑑 {2}/{3}・成品圖鑑 {4}/{5}・成就 {6}/{7}・結局 {8}/6", cleared, c.cases.length, p.hazardsSeen.length, c.hazards.length, p.productsSeen.length, c.products.length, achN, c.achievements.length, p.endings.length)),
      cont, codeBtn,
      h('div', { class: 'section-label' }, tr("選擇部門")), deptRow,
      h('div', { class: 'section-label' }, tr("選擇職務")), roleGrid,
      h('div', { class: 'section-label' }, tr("上班模式")), modeRow, tut, btnNew,
      /Line\//i.test(navigator.userAgent) ? h('a', { class: 'line-open', href: (() => { const u = new URL(location.href); u.searchParams.set('openExternalBrowser', '1'); return u.toString(); })() }, tr("📱 在 LINE 裡畫面比較小：點這裡改用手機瀏覽器開啟（可以全螢幕）")) : null,
      h('p', { class: 'help' }, isTouchMode() ? tr("🎮 手機操作：左下搖桿走路（也可以點地板）・Ⓐ 互動（說話、使用、開門、拿東西）・⇄ 旁邊東西很多時換一個・🚶／🏃 走跑切換・兩指捏合縮放。") : tr("移動：WASD／方向鍵，或點地板（自動走合規動線，黃色小點帶路）。按住 Shift 跑步（R 切換）。E 互動：說話、撿東西、拿籃子（可以往上疊）、推／拿烏龜車、推開冰箱門。Tab 換互動對象・Q 提示・T 任務欄（打開／收起）・B 背包・O 訂單・H 手冊・M 地圖・滾輪縮放。")),
      h('div', { class: 'row' }, sound, look, this.styleSwitch(), reset),
      h('div', { class: 'row fb-links' }, (() => { const b = h('button', { type: 'button', class: 'linkbtn' }, tr("🐞 回報問題")); b.onclick = () => this.feedback('bug'); return b; })(), (() => { const b = h('button', { type: 'button', class: 'linkbtn' }, tr("💡 我有好玩的想法")); b.onclick = () => this.feedback('idea'); return b; })()),
      h('p', { class: 'fine' }, c.game.disclaimer),
      h('p', { class: 'fine' }, tr("v{0}・content {1}・{2}", __APP_VERSION__, c.game.contentVersion, __COMMIT__))));
    btnNew.focus({ preventScroll: true });
    if (!this.g.stage.renderer) scr.querySelector('.card')!.append(h('p', { class: 'help warn' }, tr("這個瀏覽器無法使用 WebGL，3D 場景顯示不出來。")));
  }

  /** v1.4 畫面風格切換（新版／經典版）：記在這台瀏覽器，切換後重新整理 */
  private styleSwitch(): HTMLElement {
    const nw = isNewStyle();
    const b = h('button', { type: 'button', class: 'linkbtn style-switch', title: tr("只改外觀，遊戲內容、存檔都不變") }, nw ? tr("🎨 畫面風格：新版（改回經典版）") : tr("🎨 畫面風格：經典版（改成新版）"));
    b.onclick = () => setArtStyle(nw ? 'classic' : 'new');
    return b;
  }

  private appearance(): void {
    const p = this.g.profile; const a: Appearance = { ...defaultAppearance(), ...p.appearance };
    const body = h('div', { class: 'look' });
    const row = (label: string, opts: [string, string][], cur: string, set: (v: string) => void) => {
      const r = h('div', { class: 'look-row' }, h('span', {}, label));
      for (const [v, t] of opts) { const b = h('button', { type: 'button', class: 'chip', 'aria-pressed': String(v === cur) }, t); b.onclick = () => { set(v); this.closeModal(); this.appearance(); }; r.append(b); }
      body.append(r);
    };
    row(tr("身高"), [['short', tr("小個子")], ['mid', tr("中等")], ['tall', tr("高個子")]], a.height, v => { p.appearance = { ...a, height: v as Appearance['height'] }; });
    row(tr("膚色"), SKIN.map((_s, i) => [String(i), '●'] as [string, string]), String(a.skin), v => { p.appearance = { ...a, skin: +v }; });
    row(tr("表情"), [['smile', tr("笑咪咪")], ['calm', tr("淡定")], ['determined', tr("認真")], ['sleepy', tr("想睡")]], a.face, v => { p.appearance = { ...a, face: v as Appearance['face'] }; });
    row(tr("便服顏色"), [['#e07b5a', tr("橘")], ['#5aa9e6', tr("藍")], ['#7fbf98', tr("綠")], ['#c98fd6', tr("紫")], ['#f2c84b', tr("黃")]], a.shirt, v => { p.appearance = { ...a, shirt: v }; });
    row(tr("髮色"), [['#2b2220', tr("黑")], ['#6b4a2f', tr("棕")], ['#c49a5a', tr("金")], ['#8a8f94', tr("灰")]], a.hair, v => { p.appearance = { ...a, hair: v }; });
    body.querySelectorAll('.look-row')[1]?.querySelectorAll('.chip').forEach((b, i) => ((b as HTMLElement).style.color = SKIN[i]));
    body.append(h('p', { class: 'small' }, tr("進現場後會換成部門的工作服（全白／全灰／全草綠）、口罩、髮帽與白色雨鞋。想要更活潑的正式角色圖：把 Gemini 做好的圖放進 public/assets/characters/（見 docs/ASSET_REQUESTS.md）。")));
    Save.saveProfile(p); this.g.stage.makePlayer(p.appearance, 'white', 'none', false);
    this.openModal('look', tr("人物樣式"), body);
  }

  private dayIntro(roleId: string, opts: { mode?: 'work' | 'intern'; tutorial?: boolean } = {}): void {
    const g = this.g; g.newDay(roleId, opts); g.stop();
    const intern = g.day.mode === 'intern';
    const d = g.day, c = g.c, role = c.roleById.get(roleId)!;
    const cases = d.caseQueue.map(id => c.caseById.get(id)!);
    // v1.1.1 開場只留重點；其餘收進「了解更多資訊」
    const details = h('div', { class: 'sections' },
      h('p', { class: 'small' }, role.description),
      h('div', { class: 'kv' }, h('span', {}, tr("部門")), h('b', {}, role.deptName), h('span', {}, tr("權限")), h('b', {}, role.authority)),
      h('div', { class: 'section-label' }, tr("任務說明")),
      h('ul', { class: 'plain small' }, ...d.missionQueue.map(id => { const m = c.missionById.get(id)!; return h('li', {}, tr("{0} {1}：{2}", m.icon, m.title, m.summary)); })),
      h('div', { class: 'section-label' }, tr("今天預計會有 {0} 件異常", cases.length)),
      h('ul', { class: 'plain small' }, ...cases.map(k => h('li', {}, tr("{0}・{1}（{2}）", c.floorById.get(k.floor)!.name, k.types[0], '★'.repeat(k.difficulty))))),
      h('div', { class: 'section-label' }, tr("這個職務的注意事項")),
      h('ul', { class: 'plain small' }, ...role.notes.map(n => h('li', {}, n))),
      h('div', { class: 'section-label' }, tr("今日挑戰（成就）")),
      h('ul', { class: 'plain small' }, ...g.profile.challenges.map(id => { const a = c.achievements.find(x => x.id === id)!; return h('li', {}, tr("{0} {1}：{2}", a.icon, a.name, a.description)); })),
      h('div', { class: 'section-label' }, tr("記住")),
      h('ul', { class: 'plain small' }, h('li', {}, c.rules.flowRule), h('li', {}, tr("受傷或死亡時可以說「{0}」回到事故前。", c.game.rewind.phrase)), h('li', {}, tr("任務欄可以打開／收起（按 T）；按「💡 提示」，地上的黃色小點會帶路。"))));
    const body = h('div', {},
      h('p', { class: 'lead' }, tr("{0}｜{1} {2}–{3}", role.name, role.shift.label, role.shift.start, role.shift.end)),
      h('div', { class: 'section-label' }, tr("第一步")),
      h('p', { class: 'intro-step' }, d.tutorial ? tr("⏰ 1F 打卡 → 🎓 4F 找人資 Grace 上課") : tr("⏰ 1F 打卡 → 👕 換裝 → 💨 氣浴室 → 🧼 洗手 → 上工")),
      intern ? h('p', { class: 'small warn' }, tr("🎒 自由模式：到處看、接別人的工作。別跑出自己的區域！")) : null,
      h('div', { class: 'section-label' }, tr("今天要做")),
      h('ul', { class: 'plain' }, ...d.missionQueue.slice(0, 6).map(id => { const m = c.missionById.get(id)!; return h('li', {}, `${m.icon} ${m.title}`); }),
        d.missionQueue.length > 6 ? h('li', { class: 'small' }, tr("……還有 {0} 項", d.missionQueue.length - 6)) : null),
      moreInfo(undefined, details));
    const go = h('button', { type: 'button', class: 'btn primary' }, tr("上工！"));
    body.append(go);
    modal('intro', tr("第 {0} 天｜{1}・{2}", d.day, role.deptName, role.name), body, () => { this.modalOpen = null; document.getElementById('intro')?.remove(); this.enter(); });
    this.modalOpen = 'intro';
    go.onclick = () => { this.modalOpen = null; document.getElementById('intro')?.remove(); this.enter(); };
    go.focus({ preventScroll: true });
  }
  private enter(): void {
    $('start').hidden = true; $('hud').hidden = false;
    lockLandscape();
    this.g.start(); this.refresh();
    this.toast(tr("目標：{0}", objective(this.g)));
  }

  // ---------------------------------------------------------------- 視窗
  /** v1.0 作業視窗（小遊戲）：打開時遊戲暫停；一定要按 ✕ 才關 */
  private loadingGame = false;
  work(gameId: string, params: Record<string, unknown>, done: (r: MiniResult | null) => void, title?: string): void {
    const def = this.g.c.minigameById.get(gameId);
    if (!hasGame(gameId)) { this.toast(tr("這個作業還沒做好：{0}", gameId)); done(null); return; }
    // v1.4.1 小遊戲用到才下載：還沒下載好就先下載（通常開機後已經在背景抓好了）
    const game = gameNow(gameId);
    if (!game) {
      if (this.loadingGame) return;
      this.loadingGame = true; this.toast(tr("作業準備中…"));
      loadGame(gameId).then(gm => { this.loadingGame = false; if (gm) this.work(gameId, params, done, title); else { this.toast(tr("這個作業還沒做好：{0}", gameId)); done(null); } })
        .catch(() => { this.loadingGame = false; this.toast(tr("作業下載失敗，請檢查網路後再試一次。")); done(null); });
      return;
    }
    if (this.open) this.close();
    const was = this.g.paused; this.g.paused = true;
    const d = this.g.day;
    const seed = (d?.day ?? 1) * 7919 + (d?.clock ?? 0) * 31 + gameId.length * 977 + Object.keys(d?.missions ?? {}).length;
    openWork({ game, title: title ?? def?.name ?? gameId, icon: def?.icon ?? '🛠️', params, seed, roleId: d?.roleId ?? '',
      onClose: r => { this.g.paused = was; $('c').focus({ preventScroll: true }); done(r); this.refresh(); } });
  }
  /** v1.0 新手教學：4F 人資 Grace 的教育訓練（作業視窗；要做完小測驗、按 ✕ 才算上完） */
  training(done: () => void): void {
    if (this.open) this.close();
    const was = this.g.paused; this.g.paused = true;
    const d = this.g.day;
    openWork({ game: trainingGame(this.g), title: tr("新人教育訓練"), icon: '🎓', params: {}, seed: (d?.day ?? 1) * 101, roleId: d?.roleId ?? '',
      onClose: r => { this.g.paused = was; $('c').focus({ preventScroll: true }); if (r?.ok) done(); else this.toast(tr("教育訓練還沒上完，再找人資 Grace 一次。")); this.refresh(); } });
  }
  /** v0.9 玩家回饋表單（打開時遊戲暫停） */
  feedback(kind: FeedbackKind): void {
    if (this.modalOpen) this.closeModal();
    const was = this.g.paused; this.g.paused = true; this.fbPaused = was;
    openFeedback({ g: this.g, toast: m => this.toast(m), close: () => this.closeModal(),
      open: (id, title, body) => { this.modalOpen = id; modal(id, title, body, () => this.closeModal(), true); } }, kind);
  }
  private fbPaused: boolean | null = null;
  private closeModal() {
    if (this.modalOpen?.startsWith('feedback-') && this.fbPaused !== null) { this.g.paused = this.fbPaused; this.fbPaused = null; }
    if (!this.modalOpen) return;
    const id = this.modalOpen; this.modalOpen = null; document.getElementById(id)?.remove();
    if (id === 'intro') this.enter();
    $('c').focus({ preventScroll: true }); this.refresh();
  }
  private openModal(id: string, title: string, body: Node, wide = false) {
    if (this.open) return;
    if (this.modalOpen) { document.getElementById(this.modalOpen)?.remove(); }
    this.modalOpen = id; modal(id, title, body, () => this.closeModal(), wide);
  }

  stairs(fromId: string): void {
    const g = this.g;
    const eq = g.rt().equipment.get(fromId)!.data;
    if (eq.interaction?.type !== 'stairs') return;
    const it = eq.interaction;
    const floors = it.to;
    const list = h('div', { class: 'floors' });
    for (const fid of [...floors].sort((a, b) => g.c.floorById.get(b)!.order - g.c.floorById.get(a)!.order)) {
      const f = g.c.floorById.get(fid)!;
      const k = activeCase(g); const cur = currentMission(g);
      const pin = (k && k.floor === f.id) || cur?.st.floor === f.id;
      const b = h('button', { type: 'button', class: 'floor-btn' }, h('b', {}, f.id), h('span', {}, h('strong', {}, f.name), h('small', {}, f.subtitle)),
        pin ? h('em', { class: 'pin' }, '!') : null);
      b.onclick = () => { this.closeModal(); g.useStairs(f.id, fromId); };
      list.append(b);
    }
    list.append(h('p', { class: 'small' }, it.lift ? tr("非食品生產部門電梯：辦公室、倉儲、品保、RD 的人上下樓用，1F～4F 同一個位置。不能載食品原料。") : it.group === 'b1' ? tr("這座樓梯只通 B1 常溫倉。") : tr("廠內只有這一座主樓梯。物料走電梯（原料／半成品電梯、原料發料・成品電梯）。")));
    this.openModal('stairs', it.hidden ? tr("往頂樓的窄樓梯") : it.lift ? tr("🛗 非食品生產部門電梯") : eq.name, list);
  }
  /** 💡 提示：現在該做什麼、怎麼去 */
  hint(): void {
    const g = this.g, d = g.day; if (!d) return;
    const items: string[] = [];
    const k = activeCase(g);
    if (!d.clockedIn) items.push(tr("到 1F 大門旁（室外）的打卡鐘按 E 打卡。"));
    else if (k && d.cases[k.id].stage !== 'done') items.push(objective(g));
    const cur = currentMission(g);
    if (cur) items.push(tr("{0} {1}（第 {2}／{3} 步）：{4}", cur.m.icon, cur.m.title, cur.index + 1, cur.m.steps.length, cur.st.say), `💡 ${missionHint(g, cur.st)}`, tr("目標：{0}・{1}", cur.st.floor, targetName(g, cur.st)));
    const z = g.rt().zoneAt(...g.playerPos());
    const flow = [tr("⏰ 打卡（1F 室外大門旁）"), tr("🥼 更衣室換工作服、口罩、髮帽、雨鞋"), tr("💨 氣浴室（進場方向會吹風）"), tr("🧼 洗手 → 酒精 → 烘手機"), tr("🚪 推開門進生產區")];
    this.card({ name: tr("💡 提示"), role: z ? tr("你在：{0}・{1}", d.floor, z.name) : d.floor, color: '#f2c84b',
      text: items.join('\n') || tr("今天的工作都做完了！到處逛逛，或下班打卡。"),
      sections: [{ label: tr("進生產區的順序"), items: flow, tone: 'info' }, { label: tr("小技巧"), items: [tr("地上的黃色小點會帶你走到目標（可以在選單關掉）。"), tr("靠近東西按 E（手機點畫面下方的黃色按鈕）。"), tr("點地板就會自動走過去；按住 Shift 可以跑步（生產區別讓品保看到）。")] }],
      choices: [{ text: tr("知道了"), tone: 'primary', fn: () => this.close() }, { text: tr("🎮 作業在哪裡"), fn: () => { this.close(); this.jobBoard(); } }] });
  }


  orders(): void {
    const g = this.g, d = g.day, c = g.c;
    const rows = d.orders.lines.map(l => {
      const p = c.productById.get(l.product)!; const ln = d.lines[l.product];
      const pct = Math.min(100, Math.round((l.produced - l.scrapped) / Math.max(1, l.target) * 100));
      const fb = flowBlocked(g, l.product);
      const status = ln.stoppedBy ? tr("⏸ 停線（{0}）", ln.stoppedBy) : fb ? tr("⏸ 停線（{0}）", fb) : d.clock < ln.reworkUntil ? tr("🔁 重工中（到 {0}）", fmtClock(ln.reworkUntil)) : d.clock < ln.changeoverUntil ? tr("🔄 換線中") : l.produced >= l.target ? tr("✓ 完成") : tr("▶ 生產中");
      return h('div', { class: 'order' },
        h('div', { class: 'order-head' }, h('b', {}, (() => { const pair = menuToday(g, l.product); return pair ? tr("{0}（換線款：{1}）", c.menuById.get(pair[ln.alt ? 1 : 0])!.name, c.menuById.get(pair[ln.alt ? 0 : 1])!.name) : ln.alt ? p.alt.name : p.name; })()), h('small', {}, tr("{0}・{1}", p.floor, status))),
        h('div', { class: 'bar' }, h('em', { style: { width: `${pct}%` } })),
        h('small', {}, tr("{0} / {1}{2}（預估 {3}）{4}{5}", Math.round(l.produced - l.scrapped), l.target, p.unit, l.forecast, l.scrapped ? tr("・報廢 {0}", Math.round(l.scrapped)) : '', l.suspect ? tr("・可疑品 {0}", Math.round(l.suspect)) : '')),
        h('small', { class: 'flow-line' }, lineStatus(g, l.product)));
    });
    const body = h('div', {},
      h('p', { class: 'lead' }, d.orders.official ? tr("正式訂單已確認。") : tr("目前是預估訂單；正式訂單 {0} 由系統傳入，數量可能不同。", c.game.time.officialOrderAt)),
      h('p', { class: 'small' }, tr("生管依訂單開工單，產線照工單生產：成品一籃一籃放到線尾的收料車（烏龜車最多 5 籃），滿了推進成品電梯、用無線電通知出貨倉儲課；每一籃都要用一個乾淨的空籃，空籃不夠就停線。今天已出貨 {0} 籃。", d.flow?.shipped ?? 0)),
      ...rows,
      h('p', { class: 'small' }, tr("總達成率 {0}%。案件處理中「沒有停線」的產品會變成可疑品；壞結局時要隔離重工（約產能 1～2 倍時間）或報廢。", Math.round(completion(g) * 100))),
      h('div', { class: 'section-label' }, tr("代工廠的一天")),
      h('ul', { class: 'plain small' }, ...c.rules.dailyRhythm.map(r => h('li', {}, tr("{0}｜{1}", r.time, r.text)))));
    this.openModal('orders', tr("訂單看板"), body);
  }

  /** 🎒 背包（v0.5）：工作用品（任務給的單子、樣品……）、道具、撿到的東西、手上搬的 */
  bag(): void {
    const g = this.g, c = g.c, d = g.day;
    const owned = [...g.profile.items, ...d.items.held];
    const slot = (icon: Node | string, name: string, text: string, extra?: Node | null) => h('div', { class: 'slot' }, h('div', { class: 'slot-icon' }, icon), h('div', { class: 'slot-text' }, h('b', {}, name), h('small', {}, text), extra ?? null));
    const empty = (t: string) => h('p', { class: 'small empty' }, t);
    const pack = d.pack ?? [];
    const hs = handStack(d);
    const body = h('div', { class: 'backpack' },
      h('div', { class: 'section-label' }, tr("📄 工作用品（{0}）", pack.length)),
      h('div', { class: 'slots' }, ...(pack.length ? pack.map(id => { const it = c.packById.get(id); return slot(it?.icon ?? '📄', it?.name ?? id, it?.text ?? '',
        h('em', {}, (() => { for (const mid of d.missionQueue) { const st = missionStep(g, mid); if (st && stepNeeds(st).includes(id)) return tr("→ 要交到：{0}・{1}", st.floor, targetName(g, st)); } return ''; })())); })
        : [empty(tr("任務會給你交接單、盤點表、樣品袋……都會放在這裡。做到「交出去」那一步時會自動拿出來。"))])),
      h('div', { class: 'section-label' }, tr("🎟️ 福利社買的東西")),
      h('div', { class: 'slots' }, ...(Object.entries(g.profile.stash ?? {}).filter(([, n]) => n > 0).length ? Object.entries(g.profile.stash ?? {}).filter(([, n]) => n > 0).map(([id, n]) => {
        const it = c.game.progression.shop.find(x => x.id === id);
        const use = h('button', { type: 'button', class: 'chip' }, tr("使用"));
        use.onclick = () => this.useStash(id);
        return slot(it?.icon ?? '🎁', `${it?.name ?? id} ×${n}`, it?.text ?? '', use);
      }) : [empty(tr("4F 茶水間的福利社可以用福利點換能量飲料、食譜卡、加菜券、老鼠雷達。"))])),
      h('div', { class: 'section-label' }, tr("🧰 道具（{0}）", owned.length)),
      h('div', { class: 'slots' }, ...(owned.length ? owned.map(id => { const it = c.itemById.get(id)!; const img = `items/${it.id}.webp`;
        return slot(hasAsset(img) ? h('img', { class: 'icon-img', src: assetUrl(img), alt: '' }) : it.icon, it.name, it.effectText,
          id === 'guaiguai' && d.items.guaiguai ? h('em', {}, tr("目前放在：{0}・{1}", d.items.guaiguai.floor, c.floorById.get(d.items.guaiguai.floor)!.equipment.find(e => e.id === d.items.guaiguai!.equipment)?.name)) : null); })
        : [empty(tr("還沒有道具。聽說頂樓加蓋有些奇怪的東西……可是 5F 的樓梯在哪？"))])),
      h('div', { class: 'section-label' }, tr("🫳 撿到的東西（{0}/6）", d.bag.length)),
      h('div', { class: 'slots' }, ...(d.bag.length ? d.bag.map(t => { const p = c.pickupById.get(t)!;
        const how = { bin: tr("丟垃圾桶"), report: tr("交給品保或工務"), deliver: tr("送回該去的地方"), keep: tr("先收著") }[p.action];
        return slot(hasAsset(p.image) ? h('img', { class: 'icon-img', src: assetUrl(p.image), alt: '' }) : p.icon, p.name, p.lesson, h('em', {}, tr("處理：{0}", how))); })
        : [empty(tr("空的。地上有小圖示的東西都可以撿。"))])),
      h('div', { class: 'section-label' }, tr("🙌 手上搬的")),
      d.holding ? h('p', { class: 'small' }, d.holding.kind === 'cart' ? tr("一台空的烏龜車。") : tr("籃子 {0} 籃：{1}（由下往上）。要放到烏龜車、架子、產線旁，或疊到別的籃子上（一疊最多 6 籃）。", hs.length, hs.map(b => c.ingredientById.get(d.baskets[b].content)?.name ?? tr("籃子")).join(tr("、"))))
        : d.pushing ? h('p', { class: 'small' }, tr("推著一台烏龜車（{0} 籃）。", (d.carts[d.pushing].load?.baskets ?? 0) + d.carts[d.pushing].baskets.length)) : empty(tr("沒有。")),
      h('details', {}, h('summary', {}, tr("還沒拿到的道具")), h('ul', { class: 'plain small' }, ...c.items.filter(i => !owned.includes(i.id)).map(i => h('li', {}, tr("？？？｜線索：{0}", i.source))))),
      h('p', { class: 'fine' }, c.game.disclaimer));
    this.openModal('bag', tr("🎒 背包"), body);
  }

  /** v0.6 貨架放大檢視：點東西就拿（食材＝一籃拿在手上，物品＝放進背包）；點旁邊空白處關閉 */
  shelfView(eqId: string): void {
    const g = this.g, c = g.c; const e = g.rt().equipment.get(eqId)?.data; if (!e) return;
    const items = e.stock ?? [];
    // v0.7：物品的擺放位置（可以拖拉）。每排 3 格，最後多一排空格可以放。
    const slotsN = Math.ceil(items.length / 3) * 3 + 3;
    const layout = (): (string | null)[] => {
      g.day.shelfLayout ??= {};
      let L = g.day.shelfLayout[eqId];
      const ok = L && L.length === slotsN && items.every(id => L!.filter(x => x === id).length === 1) && L.every(x => x === null || items.includes(x));
      if (!ok) { L = [...items, ...Array(slotsN - items.length).fill(null)]; g.day.shelfLayout[eqId] = L; }
      return L!;
    };
    const body = h('div', { class: 'shelf-view' });
    const take = (id: string, render: (m: string) => void) => {
      const ing = c.ingredientById.get(id); const pk = c.packById.get(id);
      const err = takeFromShelf(g, eqId, id);
      if (err) { render(`🙅 ${err}`); return; }
      render(ing ? tr("🧺 拿了一籃「{0}」（手上 {1}／{2} 籃）", ing.name, handStack(g.day).length, HAND_MAX) : tr("🎒 「{0}」放進背包了", pk?.name ?? id));
      this.refresh();
    };
    const render = (msg = '') => {
      clear(body);
      const L = layout();
      body.append(h('p', { class: 'small' }, `${e.info ?? ''}${g.rt().zoneAt(e.pos[0], e.pos[1])?.cold ? tr("　❄️ 冷到手會痛，拿了就走。") : ''}`));
      const shelf = h('div', { class: 'shelf' });
      for (let r = 0; r < L.length; r += 3) {
        const row = h('div', { class: 'shelf-row' });
        for (let k = r; k < r + 3; k++) {
          const id = L[k];
          const slot = h('div', { class: 'shelf-slot', 'data-slot': String(k) });
          if (id) {
            const ing = c.ingredientById.get(id); const pk = c.packById.get(id);
            const left = shelfLeft(g, eqId, id);
            const img = ing && hasAsset(ing.image) ? h('img', { src: assetUrl(ing.image), alt: '', draggable: 'false' }) : h('span', { class: 'shelf-emoji', style: ing?.color ? { background: ing.color } : {} }, ing ? ingEmoji(id) : pk?.icon ?? '📦');
            const it = h('div', { class: 'shelf-item' + (left <= 0 ? ' empty' : ''), role: 'button', tabindex: '0', 'data-id': id, title: tr("點一下＝拿；按住拖拉＝換位置") },
              img, h('b', {}, ing?.name ?? pk?.name ?? id), h('small', {}, left > 0 ? tr("剩 {0}{1}{2}", left, ing ? tr(" 籃") : tr(" 份"), ing ? tr("・{0}", ing.storage) : '') : tr("拿完了")));
            it.onkeydown = ev => { if ((ev.key === 'Enter' || ev.key === ' ') && left > 0) { ev.preventDefault(); take(id, render); } };
            this.shelfDrag(it, k, left > 0, {
              take: () => take(id, render),
              drop: (to: number | 'hand') => {
                if (to === 'hand') { if (left > 0) take(id, render); else render(tr("🙅 這一格已經拿完了")); return; }
                if (to === k) { render(); return; }
                const other = L[to]; L[to] = id; L[k] = other;
                const nm = ing?.name ?? pk?.name ?? id;
                render(other ? tr("↔️ 「{0}」和「{1}」換位置了", nm, c.ingredientById.get(other)?.name ?? c.packById.get(other)?.name ?? other) : tr("➡️ 把「{0}」移到第 {1} 層第 {2} 格", nm, Math.floor(to / 3) + 1, to % 3 + 1));
              },
            });
            slot.append(it);
          } else slot.append(h('div', { class: 'shelf-hole' }, tr("空位")));
          row.append(slot);
        }
        shelf.append(row);
      }
      // v1.1 拿錯了可以放回去
      const back = returnables(g, eqId);
      const backRow = back.length ? h('div', { class: 'shelf-back' }, h('b', {}, tr("↩️ 放回去：")), ...back.map(r => {
        const nm = c.ingredientById.get(r.item)?.name ?? c.packById.get(r.item)?.name ?? r.item;
        const b = h('button', { type: 'button', class: 'chip' }, r.kind === 'pack' ? tr("🎒 {0}", nm) : r.kind === 'cart' ? tr("🛒 推車上的「{0}」", nm) : tr("🧺 手上的「{0}」", nm));
        b.onclick = () => { returnToShelf(g, eqId, r); render(tr("↩️ 把「{0}」放回架上了", nm)); this.refresh(); };
        return b;
      })) : null;
      body.append(shelf, ...(backRow ? [backRow] : []), h('div', { class: 'shelf-hand', 'data-slot': 'hand' }, tr("✋ 拖到這裡＝拿走（食材拿在手上、物品放進背包）")),
        h('p', { class: 'shelf-msg' }, msg || tr("點一下東西就拿；按住拖拉可以換位置（手機也可以）。食材會變成一籃拿在手上（最多疊 3 籃），物品放進背包。")),
        h('div', { class: 'row' }, h('p', { class: 'fine' }, tr("點畫面旁邊空白處（或 Esc）關閉放大檢視。")),
          (() => { const rb = h('button', { type: 'button', class: 'btn ghost small' }, tr("↺ 恢復原本的排法")); rb.onclick = () => { delete g.day.shelfLayout![eqId]; render(tr("↺ 恢復原本的排法了")); }; return rb; })()));
    };
    render();
    this.openModal('shelf', `🔍 ${e.name}`, body, true);
  }

  /** v0.7：貨架放大裡的拖拉（滑鼠、觸控都可以）。沒有移動就是「點一下」。 */
  private shelfDrag(el: HTMLElement, from: number, canTake: boolean, cb: { take: () => void; drop: (to: number | 'hand') => void }): void {
    el.style.touchAction = 'none';
    el.onpointerdown = (ev: PointerEvent) => {
      if (ev.button !== 0) return;
      ev.preventDefault(); ev.stopPropagation();
      const x0 = ev.clientX, y0 = ev.clientY; let ghost: HTMLElement | null = null; let over: HTMLElement | null = null;
      const r = el.getBoundingClientRect();
      try { el.setPointerCapture(ev.pointerId); } catch { /* 舊瀏覽器 */ }
      const targetAt = (x: number, y: number) => (document.elementFromPoint(x, y) as HTMLElement | null)?.closest<HTMLElement>('.shelf-slot,.shelf-hand') ?? null;
      const move = (m: PointerEvent) => {
        if (!ghost && Math.hypot(m.clientX - x0, m.clientY - y0) < 6) return;
        if (!ghost) {
          ghost = el.cloneNode(true) as HTMLElement; ghost.classList.add('shelf-ghost');
          Object.assign(ghost.style, { width: r.width + 'px', height: r.height + 'px' });
          document.body.append(ghost); el.classList.add('dragging');
        }
        ghost.style.transform = `translate(${m.clientX - r.width / 2}px, ${m.clientY - r.height / 2}px) rotate(-3deg)`;
        const t = targetAt(m.clientX, m.clientY);
        if (t !== over) { over?.classList.remove('drop-over'); over = t; over?.classList.add('drop-over'); }
      };
      const up = (u: PointerEvent) => {
        el.removeEventListener('pointermove', move); el.removeEventListener('pointerup', up); el.removeEventListener('pointercancel', up);
        over?.classList.remove('drop-over'); el.classList.remove('dragging');
        if (!ghost) { if (u.type === 'pointerup' && canTake) cb.take(); return; }
        ghost.remove();
        const t = u.type === 'pointerup' ? targetAt(u.clientX, u.clientY) : null;
        if (!t) { cb.drop(from); return; }
        cb.drop(t.dataset.slot === 'hand' ? 'hand' : Number(t.dataset.slot));
      };
      el.addEventListener('pointermove', move); el.addEventListener('pointerup', up); el.addEventListener('pointercancel', up);
    };
  }

  private useStash(id: string): void {
    const g = this.g, p = g.profile, d = g.day;
    if (!(p.stash?.[id] > 0)) return;
    const z = g.rt().zoneAt(...g.playerPos());
    if (id === 'energy-drink') {
      if (z && z.level >= 0) { this.toast(tr("🚫 生產現場不能喝飲料！到休息區、茶水間或餐廳再喝。")); return; }
      d.player.fatigue = Math.max(0, d.player.fatigue - 25); this.toast(tr("🥤 咕嚕咕嚕……精神來了！疲勞 −25"));
    } else if (id === 'recipe-card') {
      const mid = unlockRandomRecipe(g, tr("食譜卡")); if (!mid) { this.toast(tr("食譜已經全部收集完了！")); return; }
    } else if (id === 'extra-dish') { d.flags.extraDish = true; this.toast(tr("🍗 加菜券用好了：下一次在 3F 員工餐廳吃飯會加菜。")); }
    p.stash[id]--; Save.saveProfile(p); g.save(); this.closeModal(); this.bag(); this.refresh();
  }
  /** v0.6 4F 福利社：用福利點換東西 */
  shop(): void {
    const g = this.g, c = g.c, p = g.profile;
    const L = levelInfo(c.game.progression, p.xp ?? 0);
    const body = h('div', { class: 'shop' });
    const render = (msg = '') => {
      clear(body);
      body.append(h('p', { class: 'lead' }, tr("🎟️ 福利點：{0}", p.points ?? 0)), h('p', { class: 'small' }, tr("福利點怎麼來：每日任務完成、升級、成就、抓老鼠、連續上班。")));
      if (L.lv < 2) body.append(h('p', { class: 'small warn' }, tr("🔒 升到 Lv 2 才能在福利社買東西。先把今天的工作做好吧！")));
      for (const it of c.game.progression.shop) {
        const own = p.stash?.[it.id] ?? 0;
        const b = h('button', { type: 'button', class: 'btn secondary', disabled: L.lv < 2 || (p.points ?? 0) < it.price }, tr("買（{0} 點）", it.price));
        b.onclick = () => {
          p.points -= it.price; p.stash = { ...(p.stash ?? {}) };
          if (it.id === 'rat-radar') { g.day.flags.ratRadar = true; render(tr("📡 老鼠雷達開啟：今天看到老鼠的機率 ×3，小地圖會標出來。")); }
          else { p.stash[it.id] = (p.stash[it.id] ?? 0) + 1; render(tr("買了 {0} {1}！在 🎒 背包裡使用。", it.icon, it.name)); }
          bus.emit('sfx', 'item'); Save.saveProfile(p); g.save(); this.refresh();
        };
        body.append(h('div', { class: 'shop-row' }, h('span', { class: 'shop-icon' }, it.icon), h('div', {}, h('b', {}, it.name), h('small', {}, it.text), own ? h('em', {}, tr("背包裡有 {0} 個", own)) : null), b));
      }
      if (msg) body.append(h('p', { class: 'shelf-msg' }, msg));
    };
    render();
    this.openModal('shop', tr("🎟️ 福利社（4F 茶水間）"), body);
  }

  mapModal(): void {
    const cv = h('canvas', { class: 'bigmap', width: 880, height: 560 }) as HTMLCanvasElement;
    const f = this.g.c.floorById.get(this.g.day.floor)!;
    const legend = h('div', { class: 'legend' }, ...this.g.c.rules.zones.map(z => h('span', {}, h('i', { style: { background: z.color } }), z.name)),
      h('span', {}, h('i', { style: { background: '#f2b134' } }), tr("目標")), h('span', {}, h('i', { style: { background: '#e06c75' } }), tr("巡場的人")));
    this.openModal('map', tr("{0}・{1}", f.id, f.name), h('div', {}, h('p', { class: 'small' }, f.subtitle), cv, legend), true);
    drawMinimap(cv, this.g, true);
  }

  // ---------------------------------------------------------------- 存檔與進度代碼
  saves(): void {
    const g = this.g, p = g.profile;
    const list = h('div', { class: 'codes' });
    const entries = Object.entries(p.codes).sort((a, b) => b[1].savedAt - a[1].savedAt);
    for (const [code, m] of entries) {
      const row = h('div', { class: 'code-row' },
        h('div', {}, h('b', { class: 'mono' }, code), h('small', {}, tr("第 {0} 天 {1}・{2}・{3}", m.day, fmtClock(m.clock), g.c.roleById.get(m.role)?.name ?? m.role, m.label))));
      const go = h('button', { type: 'button', class: 'chip' }, tr("回到這裡"));
      go.onclick = () => { const s = Save.loadSnapshot(code); if (!s) { this.toast(tr("這個代碼的快照不在這台瀏覽器。")); return; } this.closeModal(); g.loadSnapshot(s, code); this.toast(tr("⏩ 回到 {0}（今日挑戰已重新抽選）", code)); };
      const full = h('button', { type: 'button', class: 'chip' }, tr("完整代碼"));
      full.onclick = async () => {
        const s = Save.loadSnapshot(code); if (!s) return;
        const txt = await encodeFull(s);
        const ta = h('textarea', { class: 'mono', rows: 4, readonly: true }, txt) as HTMLTextAreaElement;
        row.append(ta); ta.select();
        try { await navigator.clipboard.writeText(txt); this.toast(tr("完整代碼已複製，可以貼到別台裝置繼續。")); } catch { this.toast(tr("請手動複製下方完整代碼。")); }
      };
      row.append(h('div', { class: 'row-btns' }, go, full));
      list.append(row);
    }
    const saveNow = h('button', { type: 'button', class: 'btn primary' }, tr("💾 在這裡存一個進度點"));
    saveNow.onclick = () => { g.checkpoint(tr("手動存檔")); g.save(); this.closeModal(); this.saves(); this.toast(tr("已存檔，代碼加進清單。")); };
    const body = h('div', {},
      h('p', { class: 'small' }, tr("每個斷點（上班打卡、走樓梯、檢查機台前、事故前、手動存檔）都有一組代碼。同一台瀏覽器輸入短代碼就能回到那裡；換裝置請用「完整代碼」。代碼不包含成就：讀取代碼時「今日挑戰」會重新抽選。")),
      saveNow, this.codeInput(), h('div', { class: 'section-label' }, tr("最近的進度點（{0}）", entries.length)), list);
    this.openModal('saves', tr("存檔與進度代碼"), body);
  }
  private codeInput(): HTMLElement {
    const inp = h('input', { type: 'text', id: 'codeInput', placeholder: tr("FF-XXXX-XXXX 或 FF3:…"), class: 'mono', autocomplete: 'off' }) as HTMLInputElement;
    const btn = h('button', { type: 'button', class: 'chip' }, tr("讀取"));
    const msg = h('small', { class: 'note' });
    btn.onclick = async () => {
      const v = inp.value.trim();
      let s = isShortCode(v) ? Save.loadSnapshot(v.toUpperCase()) : await decodeFull(v);
      if (!s || !this.g.c.roleById.has(s.roleId) || s.caseQueue.some(id => !this.g.c.caseById.has(id))) { msg.textContent = tr("找不到這個代碼（短代碼只在存檔的那台瀏覽器有效；換裝置請用完整代碼）。"); return; }
      this.closeModal(); $('start').hidden = true; $('hud').hidden = false;
      this.g.loadSnapshot(s, isShortCode(v) ? v.toUpperCase() : tr("完整代碼")); this.g.start(); this.refresh();
      this.toast(tr("⏩ 讀取進度代碼（今日挑戰已重新抽選）"));
    };
    return h('div', { class: 'code-input' }, inp, btn, msg);
  }
  private codeEntry(): void { this.openModal('codeEntry', tr("輸入進度代碼"), h('div', {}, h('p', { class: 'small' }, tr("輸入短代碼（同一台瀏覽器）或完整代碼（任何裝置）。")), this.codeInput())); }

  // ---------------------------------------------------------------- 手冊
  codex(tab = 'products'): void {
    const g = this.g, c = g.c, p = g.profile;
    const tabs: [string, string][] = [['me', tr("我的成長")], ['recipes', tr("食譜收集")], ['products', tr("產線圖鑑")], ['tips', tr("小知識")], ['achievements', tr("成就")], ['hazards', tr("危害圖鑑")], ['cases', tr("案件紀錄")], ['roles', tr("角色・制服")], ['rules', tr("廠規・動線")], ['events', tr("穿插事件")], ['map', tr("樓層介紹")]];
    const content = h('div', { class: 'codex-body' });
    const nav = h('div', { class: 'tabs', role: 'tablist' });
    const show = (id: string) => {
      nav.querySelectorAll('button').forEach(b => b.setAttribute('aria-selected', String(b.dataset.tab === id)));
      clear(content);
      if (id === 'me') {
        const P = c.game.progression; const L = levelInfo(P, p.xp ?? 0);
        const stat = (l: string, v: string) => h('div', { class: 'stat' }, h('span', {}, l), h('b', {}, v));
        content.append(
          h('div', { class: 'stats' }, stat(tr("等級"), tr("Lv {0}「{1}」", L.lv, L.title)), stat(tr("經驗"), L.need ? tr("{0}／{1}（下一級）", L.into, L.need) : tr("已經滿級")), stat(tr("累積經驗"), String(p.xp ?? 0)),
            stat(tr("月薪"), `$${(p.salary ?? 0).toLocaleString()}`), stat(tr("福利點"), `🎟️ ${p.points ?? 0}`), stat(tr("連續上班"), tr("{0} 天", p.streak ?? 0)),
            stat(tr("食譜收集"), tr("{0}／{1}", (p.recipes ?? []).length, c.menu.length)), stat(tr("小知識"), tr("{0}／{1}", (p.tipsHeard ?? []).length, c.tips.length)), stat(tr("抓到老鼠"), tr("{0} 隻", p.counters.ratsDelivered ?? 0))),
          h('div', { class: 'section-label' }, tr("怎麼拿經驗")),
          h('ul', { class: 'plain small' }, ...([['step', tr("任務的每一步")], ['mission', tr("完成主線／支線任務")], ['daily', tr("完成每日任務")], ['caseGood', tr("案件好結局")], ['rat', tr("抓到老鼠")], ['recipe', tr("收集一道食譜")], ['achievement', tr("解鎖成就")], ['report', tr("通報異物、老鼠")], ['pickup', tr("撿起地上的東西")], ['wash', tr("洗手")], ['meal', tr("吃午餐")], ['chat', tr("和作業員聊天（每人每天一次）")], ['dayEnd', tr("下班")]] as [string, string][]).map(([k, t]) => h('li', {}, tr("{0}：+{1}", t, P.xp[k] ?? 0)))),
          h('div', { class: 'section-label' }, tr("升級獎勵")),
          h('ul', { class: 'plain small' }, h('li', {}, tr("每升一級：月薪 +${0}、福利點 +{1}", P.salary.perLevel, P.points.levelUp ?? 0)), ...P.perks.map(k => h('li', { class: L.lv >= k.level ? '' : 'locked' }, tr("{0} Lv {1}：{2}", L.lv >= k.level ? '✔' : '🔒', k.level, k.text)))),
          h('div', { class: 'section-label' }, tr("稱號")),
          h('p', { class: 'small' }, P.titles.map((t, i) => tr("Lv {0}～ {1}", i * 3 + 1, t)).join(tr("　"))),
          h('div', { class: 'section-label' }, tr("加薪紀錄（{0}）", (p.raises ?? []).length)),
          h('ul', { class: 'plain small' }, ...((p.raises ?? []).slice(-12).reverse().map(r => h('li', {}, tr("第 {0} 天｜+${1}｜{2}", r.day, r.amount, r.why))))),
          h('p', { class: 'fine' }, tr("下一級還要 {0} 經驗（Lv {1} → {2} 需要 {3}）。月薪、福利點都是遊戲設定。", L.need ? L.need - L.into : 0, L.lv, L.lv + 1, xpToNext(P, L.lv))));
      } else if (id === 'recipes') {
        const got = new Set(p.recipes ?? []);
        content.append(h('p', { class: 'small' }, tr("已收集 {0}／{1} 道成品。收集方法：站在產線旁邊看正在包的成品、找 4F RD 主廚 Kevin（每天教一道）、福利社的食譜卡、升級獎勵。半成品的每一站做法，要「擔任過那個部門」或「和那一站的幹部聊過」才看得到。（食譜為遊戲設定，非實際配方）", got.size, c.menu.length)));
        for (const pr of c.products) {
          const items = c.menu.filter(m => m.line === pr.id);
          content.append(h('div', { class: 'section-label' }, tr("{0}・{1}線（{2}／{3}）", pr.floor, pr.name.replace(/^.*?(御飯糰|手捲|豆皮壽司|漢堡|便當|燴飯|滷肉飯|炒飯).*$/, '$1'), items.filter(m => got.has(m.id)).length, items.length)));
          for (const m of items) {
            const ok = got.has(m.id);
            content.append(h('details', { class: 'hz recipe-card' + (ok ? '' : ' locked') },
              h('summary', {}, `${ok ? m.icon : '🔒'} ${ok ? m.name : tr("？？？")}`, h('small', {}, ` ${m.category}`)),
              ...(ok ? [h('p', { class: 'small' }, m.description),
                h('div', { class: 'parts' }, ...m.parts.map(pt => h('span', { class: 'part ' + pt.kind }, `${pt.kind === 'pack' ? '📦' : pt.semi ? '🍳' : '🛒'} ${pt.name}`))),
                ...m.parts.filter(pt => pt.semi).map(pt => { const se = c.semiById.get(pt.semi!)!;
                  return h('div', { class: 'semi' }, h('b', {}, `${se.icon} ${se.name}`),
                    h('ol', { class: 'recipe' }, ...se.steps.map(stp => p.knowledge.includes(stp.dept)
                      ? h('li', {}, h('b', {}, tr("{0}｜", stp.station)), stp.text, stp.ccp ? h('span', { class: 'ccp' }, ` ✅ ${stp.ccp}`) : null)
                      : h('li', { class: 'locked' }, tr("{0}｜？？？（去找這一站的幹部聊聊）", stp.station))))); })]
                : [h('p', { class: 'small' }, tr("到產線旁邊看看輸送帶上在包什麼，或問問 RD 主廚。"))])));
          }
        }
      } else if (id === 'tips') {
        const heard = new Set(p.tipsHeard ?? []);
        content.append(h('p', { class: 'small' }, tr("聽過 {0}／{1} 則。每個作業員、幹部都可以聊兩句；在不同的地方聊，會聽到那裡才有的小知識。", heard.size, c.tips.length)),
          h('ul', { class: 'plain small' }, ...c.tips.map(t => h('li', { class: heard.has(t.id) ? '' : 'locked' }, heard.has(t.id) ? `💡 ${t.text}` : tr("？？？（{0}）", t.where[0] === 'any' ? tr("到處") : t.where[0])))));
      } else if (id === 'products') {
        content.append(h('p', { class: 'small' }, tr("看過 {0}/{1} 種成品。流程步驟要「擔任過該部門」或「和該部門幹部聊過」才看得到。已解鎖：{2}。（食譜為遊戲設定，非實際配方）", p.productsSeen.length, c.products.length, p.knowledge.map(k => DEPT_NAME[k] ?? k).join(tr("、")) || tr("無"))));
        for (const pr of c.products) {
          const seen = p.productsSeen.includes(pr.id);
          content.append(h('details', { class: 'hz' + (seen ? '' : ' locked') },
            h('summary', {}, `${seen ? pr.name : tr("？？？")}`, h('small', {}, tr(" {0}・{1}", pr.category, pr.floor))),
            ...(seen ? [hasAsset(pr.image) ? h('img', { class: 'product-img', src: assetUrl(pr.image), alt: pr.name }) : null, h('p', {}, pr.description),
              h('div', { class: 'ingredients' }, ...pr.components.map(n => { const ig = c.ingredientByName.get(n); return h('span', { class: 'ing', title: ig?.storage ?? '' }, ig && hasAsset(ig.image) ? h('img', { src: assetUrl(ig.image), alt: '' }) : null, n); })),
              h('p', { class: 'small' }, tr("換線款：{0}", pr.alt.name)),
              h('ol', { class: 'recipe' }, ...pr.steps.map(s => p.knowledge.includes(s.dept)
                ? h('li', {}, h('b', {}, tr("{0}｜", DEPT_NAME[s.dept] ?? s.dept)), s.text)
                : h('li', { class: 'locked' }, tr("{0}｜？？？（去找這個部門的幹部聊聊）", DEPT_NAME[s.dept] ?? s.dept))))]
              : [h('p', { class: 'small' }, tr("到產線旁邊看看輸送帶上跑的是什麼。"))])));
        }
      } else if (id === 'achievements') {
        content.append(h('p', { class: 'small' }, tr("已解鎖 {0}/{1}。今日挑戰：{2}", Object.keys(p.achievements).length, c.achievements.length, p.challenges.map(x => c.achievements.find(a => a.id === x)?.name).join(tr("、")) || tr("無"))));
        const grid = h('div', { class: 'ach-grid' });
        for (const a of c.achievements) {
          const got = p.achievements[a.id];
          grid.append(h('div', { class: 'ach' + (got ? ' got' : '') + (p.challenges.includes(a.id) ? ' challenge' : '') }, h('span', { class: 'ach-icon' }, got ? a.icon : '🔒'), h('b', {}, a.name), h('small', {}, a.description), got ? h('small', { class: 'when' }, tr("第 {0} 天", got)) : null));
        }
        content.append(grid);
      } else if (id === 'hazards') {
        content.append(h('p', { class: 'small' }, tr("已解鎖 {0}/{1}。遇到相關案件或事件就會解鎖。", p.hazardsSeen.length, c.hazards.length)));
        for (const hz of c.hazards) {
          const seen = p.hazardsSeen.includes(hz.id);
          content.append(h('details', { class: 'hz' + (seen ? '' : ' locked') },
            h('summary', {}, tr("{0}｜{1}", hz.id, seen ? hz.title : tr("？？？")), h('small', {}, ` ${hz.category}`)),
            ...(seen ? [h('p', { class: 'small' }, tr("範圍：{0}", hz.scope ?? '—')),
              ...([[tr("環境補充"), hz.environment], [tr("故障因子"), hz.failure], [tr("重工因子"), hz.rework], [tr("死亡因子"), hz.fatal], [tr("工傷事件"), hz.injury], [tr("食品法規違規"), hz.regulation], [tr("正確處置"), hz.correctAction]] as [string, string[]][])
                .map(([l, arr]) => h('div', { class: 'sec' }, h('div', { class: 'sec-label' }, l), h('ul', {}, ...arr.map(x => h('li', {}, x)))))] : [h('p', { class: 'small' }, tr("尚未遇到。"))])));
        }
      } else if (id === 'cases') {
        for (const k of c.cases) {
          const r = p.cleared[k.id];
          content.append(h('div', { class: 'case-row' + (r ? '' : ' locked') }, h('b', {}, tr("{0}｜{1}", k.id, r ? k.title : tr("？？？"))), h('small', {}, tr("{0}・{1}", k.locationText, '★'.repeat(k.difficulty))),
            r ? h('span', { class: 'grade ' + r.grade }, `${r.grade === 'good' ? tr("好結局") : tr("壞結局")} ${r.best}/${r.total}`) : h('span', { class: 'grade' }, tr("未遇到"))));
        }
      } else if (id === 'roles') {
        content.append(h('div', { class: 'legend' }, ...c.rules.uniforms.map(u => h('span', {}, h('i', { style: { background: u.color, border: '1px solid #999' } }), tr("{0}：{1}", u.uniform, u.dept)))),
          h('ul', { class: 'plain small' }, ...c.rules.hats.map(x => h('li', {}, tr("{0}＝{1}", x.label, x.meaning)))));
        for (const [dept, name] of new Map(c.roles.map(r => [r.dept, r.deptName]))) {
          content.append(h('div', { class: 'section-label' }, `${DEPT_ICON[dept] ?? ''} ${name}`));
          for (const r of c.roles.filter(x => x.dept === dept)) content.append(h('details', { class: 'hz' }, h('summary', {}, r.name, h('small', {}, ` ${r.shift.label} ${r.shift.start}–${r.shift.end}`)), h('p', { class: 'small' }, r.authority), h('ul', { class: 'plain small' }, ...r.notes.map(n => h('li', {}, n)))));
        }
      } else if (id === 'rules') {
        content.append(h('div', { class: 'legend' }, ...c.rules.zones.map(z => h('span', {}, h('i', { style: { background: z.color } }), tr("{0}：{1}", z.name, z.description)))),
          h('p', {}, c.rules.flowRule), h('ul', {}, ...c.rules.rules.map(r => h('li', {}, r))), h('p', { class: 'fine' }, c.game.disclaimer));
      } else if (id === 'events') {
        content.append(h('p', { class: 'small' }, c.game.events.note ?? ''));
        for (const ev of c.events) {
          const seen = p.eventsLearned.includes(ev.id) || g.day.events.firedToday.includes(ev.id);
          content.append(h('div', { class: 'case-row' + (seen ? '' : ' locked') }, h('b', {}, tr("{0}｜{1}", ev.id, seen ? ev.title : tr("？？？"))), h('small', {}, tr("{0} 級・{1}", ev.tier, ev.locationText)),
            seen ? h('span', { class: 'small' }, tr("正確作法：{0}", ev.options[0].text)) : null));
        }
      } else {
        for (const f of [...c.floors].reverse()) content.append(h('div', { class: 'case-row' }, h('b', {}, tr("{0}｜{1}", f.id, f.name)), h('small', {}, f.subtitle),
          h('span', { class: 'small' }, f.zones.filter(z => !z.hidden || p.flags.found5F).map(z => tr("{0}（{1}）", z.name, LEVEL_NAME[z.level])).join(tr("・")))));
      }
    };
    for (const [id, label] of tabs) { const b = h('button', { type: 'button', role: 'tab', 'data-tab': id }, label); b.onclick = () => show(id); nav.append(b); }
    this.openModal('codex', tr("安全手冊・圖鑑"), h('div', {}, nav, content), true);
    show(tab);
  }

  /** v0.7：中文／English 切換（切換會重新整理，存檔不受影響） */
  private langSwitch(): HTMLElement {
    const box = h('div', { class: 'lang-switch', role: 'group', 'aria-label': 'Language' });
    for (const [l, label] of [['zh', '中文'], ['en', 'English']] as const) {
      const b = h('button', { type: 'button', class: 'lang-btn', 'aria-pressed': String(lang === l) }, label);
      b.onclick = () => { if (lang !== l) setLang(l); };
      box.append(b);
    }
    return box;
  }

  private menu(): void {
    const body = h('div', { class: 'menu' });
    const add = (t: string, fn: () => void, cls = 'btn secondary') => { const b = h('button', { type: 'button', class: cls }, t); b.onclick = fn; body.append(b); };
    add(tr("繼續遊戲"), () => this.closeModal(), 'btn primary');
    add(tr("🎮 作業在哪裡（全場的作業・可以接）"), () => { this.closeModal(); this.jobBoard(); });
    add(tr("💾 存檔與進度代碼"), () => { this.closeModal(); this.saves(); });
    add(isNewStyle() ? tr("🎨 畫面風格：新版 → 改回經典版") : tr("🎨 畫面風格：經典版 → 改成新版"), () => { this.closeModal(); if (this.g.day) { this.g.day.pos = this.g.playerPos(); this.g.save(); } setArtStyle(isNewStyle() ? 'classic' : 'new'); });
    add(tr("「{0}」回到上一個檢查點（{1}）", this.g.c.game.rewind.phrase, this.g.meta.checkpoints.at(-1)?.label ?? tr("無")), () => { this.closeModal(); this.g.rewind(); });
    add(this.g.profile.settings.guide ? tr("🟡 關掉地上的導航小點") : tr("🟡 打開地上的導航小點"), () => { this.g.profile.settings.guide = !this.g.profile.settings.guide; Save.saveProfile(this.g.profile); this.closeModal(); this.toast(this.g.profile.settings.guide ? tr("導航小點：開") : tr("導航小點：關")); });
    add(tr("🐞 回報問題（異常）"), () => { this.closeModal(); this.feedback('bug'); });
    add(tr("💡 我有好玩的想法（需求）"), () => { this.closeModal(); this.feedback('idea'); });
    add(lang === 'en' ? '🌐 切換成中文' : '🌐 Switch to English', () => { this.closeModal(); this.g.day.pos = this.g.playerPos(); this.g.save(); setLang(lang === 'en' ? 'zh' : 'en'); });
    add(tr("回到標題畫面（自動存檔）"), () => { this.closeModal(); this.g.day.pos = this.g.playerPos(); this.g.save(); this.showStart(); });
    body.append(h('p', { class: 'small' }, tr("已使用重生 {0} 次。", this.g.meta.rewinds)));
    this.openModal('menu', tr("選單"), body);
  }

  /** v1.3「🎮 作業在哪裡」：全場的作業小遊戲在哪一樓、哪一區（平面圖＋清單）。可以「📍 帶我去」或直接接下 */
  jobBoard(floor?: string): void {
    const g = this.g, d = g.day, c = g.c;
    const sites = jobSites(g);
    const floors = [...new Set(sites.map(s => s.floor))];
    let cur = floor && floors.includes(floor) ? floor : floors.includes(d.floor) ? d.floor : floors[0];
    const DEPT: Record<string, string> = { production: tr("生產部"), qa: tr("品保部"), warehouse: tr("倉儲部"), purchasing: tr("採購"), planning: tr("生管"), rd: tr("研發部"), engineering: tr("工務部"), facility: tr("廠務"), hr: tr("人資"), other: tr("其他") };
    const body = h('div', { class: 'jobmap' });
    const tabs = h('div', { class: 'jm-tabs', role: 'tablist' });
    const cv = h('canvas', { class: 'jm-map', width: 880, height: 480 }) as HTMLCanvasElement;
    const list = h('div', { class: 'jm-list' });
    body.append(h('p', { class: 'small' }, tr("綠色數字＝這裡有作業。按「📍 帶我去」，地上的黃色小點會帶路；也可以直接按「接下」。")), tabs, cv, list);
    const render = () => {
      clear(tabs); clear(list);
      for (const f of floors) {
        const n = sites.filter(s => s.floor === f).length;
        const b = h('button', { type: 'button', class: 'chip' + (f === cur ? ' on' : ''), role: 'tab', 'aria-selected': String(f === cur) }, tr("{0}（{1}）", f, n), f === d.floor ? ' 📍' : '');
        b.onclick = () => { cur = f; render(); }; tabs.append(b);
      }
      const here = sites.filter(s => s.floor === cur);
      this.drawJobMap(cv, cur, here);
      let lastZone = '';
      here.forEach((s, i) => {
        if (s.zone !== lastZone) { lastZone = s.zone; list.append(h('div', { class: 'section-label' }, tr("{0}・{1}", s.floor, s.zone || tr("其他")))); }
        const def = c.minigameById.get(s.game); const pr = d.missions[s.m.id];
        const kind = missionKind(g, s.m);
        const row = h('div', { class: 'jm-row' + (pr?.done ? ' done' : '') },
          h('span', { class: 'jm-no' }, String(i + 1)),
          h('div', { class: 'jm-text' }, h('b', {}, `${s.m.icon} ${s.m.title}`), h('small', {}, tr("{0}｜{1}・{2}", s.name, DEPT[def?.dept ?? 'other'] ?? '', kind === 'side' ? tr("支線") : tr("主線")))));
        const btns = h('div', { class: 'jm-btns' });
        const go = h('button', { type: 'button', class: 'chip' }, tr("📍 帶我去"));
        go.onclick = () => { d.waypoint = { floor: s.floor as never, target: s.target, label: s.name }; this.closeModal(); g.updateSigns(); this.refresh(); this.toast(s.floor === d.floor ? tr("📍 跟著地上的黃色小點走到「{0}」", s.name) : tr("📍「{0}」在 {1}：先跟著小點走到樓梯", s.name, s.floor)); };
        btns.append(go);
        if (pr) btns.append(h('b', { class: 'jm-state' }, pr.failed ? tr("✘ 失敗") : pr.done ? tr("✔ 完成") : tr("已接")));
        else { const b = h('button', { type: 'button', class: 'chip primary' }, tr("接下")); b.onclick = () => { this.closeModal(); takeJob(g, s.m); this.refresh(); }; btns.append(b); }
        row.append(btns); list.append(row);
      });
    };
    render();
    this.openModal('jobs', tr("🎮 作業在哪裡"), body, true);
  }
  /** 作業地圖：分區、設備、作業位置（綠色數字）、自己的位置 */
  private drawJobMap(cv: HTMLCanvasElement, floorId: string, here: JobSite[]): void {
    const g = this.g; const f = g.c.floorById.get(floorId as never); const x = cv.getContext('2d'); if (!f || !x) return;
    const [W, D] = f.size; const s = Math.min(cv.width / W, cv.height / D);
    const ox = (cv.width - W * s) / 2, oz = (cv.height - D * s) / 2;
    const X = (v: number) => ox + (v + W / 2) * s, Z = (v: number) => oz + (v + D / 2) * s;
    x.clearRect(0, 0, cv.width, cv.height); x.fillStyle = isNewStyle() ? '#2e2621' : '#0e161b'; x.fillRect(0, 0, cv.width, cv.height);
    for (const z of f.zones) {
      if (z.hidden) continue;
      x.fillStyle = (floorColorNew(floorKind(z)) ?? FLOOR_COLOR[floorKind(z)]) + (z.level < 0 && !z.outdoor ? '55' : 'aa');
      x.fillRect(X(z.rect[0]), Z(z.rect[1]), z.rect[2] * s, z.rect[3] * s);
    }
    x.strokeStyle = '#cfd8dc'; x.lineWidth = 2;
    for (const w of f.walls) { x.beginPath(); x.moveTo(X(w[0]), Z(w[1])); x.lineTo(X(w[2]), Z(w[3])); x.stroke(); }
    x.fillStyle = '#9aa7ad88';
    for (const e of f.equipment) if (e.kind !== 'floorZone') x.fillRect(X(e.pos[0] - e.size[0] / 2), Z(e.pos[1] - e.size[2] / 2), e.size[0] * s, e.size[2] * s);
    x.textAlign = 'center'; x.textBaseline = 'middle';
    x.font = `700 ${Math.max(11, s * 0.55)}px sans-serif`; x.fillStyle = '#e8eef0';
    for (const z of f.zones) if (!z.hidden && z.rect[2] * s > 60) x.fillText(z.name.split(tr("（"))[0].trim(), X(z.rect[0] + z.rect[2] / 2), Z(z.rect[1]) + Math.max(10, s * 0.5));
    const r = Math.max(9, s * 0.55);
    here.forEach((q, i) => {
      const wp = g.day.waypoint; const hot = wp && wp.floor === q.floor && wp.target === q.target;
      x.fillStyle = hot ? '#f2c84b' : '#3f9e6c'; x.strokeStyle = '#ffffff'; x.lineWidth = 2;
      x.beginPath(); x.arc(X(q.x), Z(q.z), r, 0, Math.PI * 2); x.fill(); x.stroke();
      x.fillStyle = hot ? '#18242c' : '#ffffff'; x.font = `900 ${Math.round(r * 1.1)}px sans-serif`; x.fillText(String(i + 1), X(q.x), Z(q.z) + 1);
    });
    if (g.day.floor === floorId) {
      const [px, pz] = g.playerPos();
      x.fillStyle = '#ffffff'; x.strokeStyle = '#3f78a8'; x.lineWidth = 3;
      x.beginPath(); x.arc(X(px), Z(pz), r * 0.7, 0, Math.PI * 2); x.fill(); x.stroke();
      x.fillStyle = '#ffffff'; x.font = `700 ${Math.max(11, s * 0.5)}px sans-serif`; x.fillText(tr("你"), X(px), Z(pz) - r * 1.4);
    }
    x.textAlign = 'start'; x.textBaseline = 'alphabetic';
  }

  // ---------------------------------------------------------------- 結局
  ending(): void {
    const g = this.g, d = g.day, c = g.c;
    if (d.ended) return;
    const res = evaluate(g);
    const end = c.game.endings[res.id]!;
    if (!g.profile.endings.includes(res.id)) g.profile.endings.push(res.id);
    g.endDay(); check(g); this.close();
    const comp = completion(g);
    const stat = (l: string, v: string) => h('div', { class: 'stat' }, h('span', {}, l), h('b', {}, v));
    const overtime = Math.max(0, d.clock - d.shiftEnd);
    const todayAch = Object.entries(g.profile.achievements).filter(([, day]) => day === d.day).map(([id]) => c.achievements.find(a => a.id === id)!);
    const body = h('div', { class: 'ending' },
      h('div', { class: 'end-line' }, h('small', {}, end.speaker), h('p', {}, tr("「{0}」", end.line))),
      h('ul', { class: 'plain' }, ...res.reasons.map(r => h('li', {}, r))),
      h('div', { class: 'stats' },
        stat(tr("下班時間"), `${fmtClock(d.clock)}${overtime ? tr("（加班 {0}）", fmtDuration(overtime)) : ''}`), stat(tr("訂單達成"), `${Math.round(comp * 100)}%`),
        stat(tr("食安"), String(Math.round(d.factory.foodSafety))), stat(tr("紀錄完整度"), String(Math.round(d.factory.records))),
        stat(tr("稽核分數"), String(Math.round(d.factory.audit))), stat(tr("信任"), String(Math.round(d.factory.trust))),
        stat(tr("罰款"), `$${d.factory.fines}`), stat(tr("動線違規"), tr("{0} 次被抓／{1} 次沒人看到", d.factory.caught, d.factory.unseen)),
        stat(tr("工傷"), d.injuries.length ? d.injuries.map(i => i.label).join(tr("、")) : '0'), stat(tr("重生"), tr("{0} 次", g.meta.rewinds)),
        stat(tr("任務"), `${d.missionQueue.filter(id => d.missions[id].done).length}/${d.missionQueue.length}`), stat(tr("走過的樓層"), d.floorsVisited.join(tr("、")))),
      (() => { const pc = paycheck(g); const L = levelInfo(c.game.progression, g.profile.xp ?? 0); const e = d.earn ?? { xp: 0, points: 0, raise: 0, notes: [] };
        return h('div', { class: 'payslip' }, h('div', { class: 'section-label' }, tr("💴 今天的薪資單")),
          h('div', { class: 'stats' }, stat(tr("日薪"), `$${pc.daily.toLocaleString()}`), stat(tr("加班費"), `$${pc.overtime.toLocaleString()}`), stat(tr("今天合計"), `$${pc.total.toLocaleString()}`),
            stat(tr("月薪"), `$${(g.profile.salary ?? 0).toLocaleString()}${e.raise ? tr("（今天 +${0}）", e.raise) : ''}`), stat(tr("經驗"), tr("+{0}（Lv {1}「{2}」{3}）", e.xp, L.lv, L.title, L.need ? ` ${L.into}/${L.need}` : '')), stat(tr("福利點"), tr("+{0}（共 🎟️ {1}）", e.points, g.profile.points ?? 0))),
          e.notes.length ? h('ul', { class: 'plain small' }, ...e.notes.slice(0, 12).map(n => h('li', {}, n))) : null); })(),
      todayAch.length ? h('div', { class: 'section-label' }, tr("今天解鎖的成就")) : null,
      todayAch.length ? h('ul', { class: 'plain' }, ...todayAch.map(a => h('li', {}, `${a.icon} ${a.name}`))) : null,
      h('div', { class: 'section-label' }, tr("案件")),
      h('ul', { class: 'plain' }, ...d.caseQueue.map(id => { const k = c.caseById.get(id)!, pr = d.cases[id]; return h('li', {}, tr("{0}｜{1}：{2}", id, k.title, pr.grade ? (pr.grade === 'good' ? tr("✔ 好結局") : tr("✘ 壞結局")) : tr("未完成"))); })),
      h('details', {}, h('summary', {}, tr("今天的紀錄")), h('ul', { class: 'plain small' }, ...d.log.map(l => h('li', { class: l.kind }, `${fmtClock(l.at)} ${l.text}`)))));
    const next = h('button', { type: 'button', class: 'btn primary' }, tr("下一天"));
    next.onclick = () => { this.closeModal(); g.nextDay(); this.showStart(); };
    body.append(next);
    this.modalOpen = 'ending';
    modal('ending', end.title, body, () => { this.closeModal(); g.nextDay(); this.showStart(); }, true);
    bus.emit('sfx', res.id === 'perfect' || res.id === 'guaiguai' || res.id === 'normal' ? 'ok' : 'alarm');
  }
}

/** 食材的小圖示（沒有圖片時用） */
const ING_EMOJI: Record<string, string> = {
  rice: '🍚', 'sushi-rice': '🍚', 'glutinous-rice': '🍚', 'salmon-flake': '🐟', salmon: '🐟', mackerel: '🐟', 'tuna-can': '🥫', 'tuna-salad': '🥫', nori: '🟩', sesame: '⚪',
  'inari-skin': '🟫', bun: '🍔', toast: '🍞', 'chicken-thigh': '🍗', teriyaki: '🍯', lettuce: '🥬', cabbage: '🥬', basil: '🌿', tomato: '🍅', egg: '🥚', 'braised-egg': '🥚',
  scallion: '🌱', onion: '🧅', garlic: '🧄', ginger: '🫚', cucumber: '🥒', corn: '🌽', kimchi: '🌶️', 'pickled-mustard': '🥬', 'pickled-radish': '🟨',
  'beef-slice': '🥩', 'pork-rib': '🍖', 'pork-loin': '🥩', 'pork-mince': '🥩', 'braised-pork': '🥘', bacon: '🥓', ham: '🥓', shrimp: '🦐', 'crab-stick': '🦀', tofu: '⬜', cheese: '🧀',
  'soy-sauce': '🍶', 'rice-wine': '🍶', 'sushi-vinegar': '🍶', 'salad-oil': '🫒', 'sesame-oil': '🫒', sugar: '🧂', mayo: '🥚', 'black-pepper-sauce': '🫙', 'curry-sauce': '🍛', 'curry-roux': '🍛',
  flavor: '🧪', colorant: '🧪', 'sweet-potato-starch': '🌾', 'potato-starch': '🌾', 'chicken-powder': '🌾',
  'tri-pack': '📦', 'handroll-pack': '📦', 'box-pack': '📦', 'bento-box': '🍱', 'bento-lid': '🍱', 'donburi-box': '🥡', 'friedrice-box': '🥡', 'sandwich-box': '📦', 'long-onigiri-bag': '📦',
  'seal-film': '🎞️', 'heat-shrink-film': '🎞️', 'wrap-paper': '📄', 'label-sticker': '🏷️', 'side-dishes': '🥗',
};
export const ingEmoji = (id: string) => ING_EMOJI[id] ?? '📦';
